// ─────────────────────────────────────────────────────────────────────────────
// ContextAnalytics
//
// The "Context" analytics tab. Answers "does your trading context matter?" —
// segmenting performance by the conditions you logged at trade time:
//   • News Event Performance    — is_news_event
//   • Manual Exit Cliff         — exit_reason
//   • Market Regime Edge        — market_condition
//   • Setup Edge Ranking        — setup_type
//
// Structure mirrors BehaviorAnalytics: same detector card shell, same
// acknowledge/dismiss/working-on-it system, same sparkline history.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useMemo, useState } from "react";
import { Card } from "../../components/ui/Card";
import { SectionTitle } from "../../components/ui/SectionTitle";
import { Pill } from "../../components/ui/Pill";
import { C } from "../../lib/colors";
import { fmt$ } from "../../lib/format";
import { useDetectors } from "../../hooks/useDetectors";
import { writeSnapshots, getTargetDescriptor, computeProgress } from "../../lib/detectorState";
import { useDetectorStates } from "../../hooks/useDetectorStates";
import { useDetectorSnapshotsMap, getSeries } from "../../hooks/useDetectorSnapshotsMap";
import { DetectorActionsMenu } from "./DetectorActionsMenu";
import { Sparkline } from "../../components/Sparkline";
import { useViewport } from "../../hooks/useViewport";
import { InsufficientStatePanel, EarlySignalBanner } from "../../components/DetectorProgress";

// ─── Confidence badge ────────────────────────────────────────────────────────
const CONFIDENCE_COLOR = {
  strong:       C.green,
  moderate:     C.yellow,
  thin:         C.orange || "#ff9500",
  insufficient: C.muted,
};

function ConfidenceBadge({ level }) {
  const color = CONFIDENCE_COLOR[level] || C.muted;
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: "2px 8px",
      borderRadius: 12, letterSpacing: "0.06em", textTransform: "uppercase",
      background: color + "22", border: `1px solid ${color}55`, color,
    }}>
      {level}
    </span>
  );
}

// ─── Status pill ─────────────────────────────────────────────────────────────
const STATUS_COLOR = {
  acknowledged:  C.blue,
  working_on_it: C.green,
  dismissed:     C.muted,
};

function StatusPill({ status }) {
  if (!status || status === "active") return null;
  const color = STATUS_COLOR[status] || C.muted;
  const label = status === "working_on_it" ? "Working on it" :
                status === "acknowledged"  ? "Acknowledged"  : "Dismissed";
  return (
    <span style={{
      fontSize: 10, padding: "2px 8px", borderRadius: 12,
      background: color + "22", border: `1px solid ${color}55`, color,
      fontWeight: 600,
    }}>
      {label}
    </span>
  );
}

// ─── Empty / needs-data placeholder ──────────────────────────────────────────
function NeedsData({ msg }) {
  return (
    <div style={{ padding: "16px 0", color: C.muted, fontSize: 12, fontStyle: "italic" }}>
      {msg}
    </div>
  );
}

// ─── Sparkline section ────────────────────────────────────────────────────────
function SparklineSection({ detector, snapshotsMap, sinceDays }) {
  const { isMobile } = useViewport();
  const descriptor = getTargetDescriptor(detector.id);
  if (!descriptor) return null;

  const series = getSeries(snapshotsMap, detector.id, descriptor.metric, sinceDays);
  if (!series || series.length < 2) {
    return (
      <div style={{ fontSize: 11, color: C.muted, fontStyle: "italic", paddingTop: 8 }}>
        ≥2 days needed for trend
      </div>
    );
  }

  const latest = series[series.length - 1]?.value;

  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>
        {descriptor.label} — last {sinceDays}d
      </div>
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: isMobile ? "flex-start" : "flex-end",
        flexDirection: isMobile ? "column" : "row",
        gap: 12,
      }}>
        <Sparkline
          points={series}
          width={isMobile ? undefined : 220}
          height={48}
          direction={descriptor.direction}
          formatter={descriptor.formatter}
          ariaLabel={`${detector.title} sparkline`}
          style={isMobile ? { width: "100%" } : undefined}
        />
        {latest != null && (
          <div style={{ textAlign: isMobile ? "left" : "right", minWidth: 70 }}>
            <div style={{ fontSize: 10, color: C.muted }}>latest</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.text, fontFamily: "monospace" }}>
              {descriptor.formatter(latest)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Detector-specific detail renderers
// ─────────────────────────────────────────────────────────────────────────────

function NewsEventDetails({ d }) {
  if (!d || d.n_news == null) return <NeedsData msg="No news-event trades logged yet." />;

  const isPositive = d.edge_direction === "positive";
  return (
    <div>
      <div className="fos-grid-2col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
        <StatBlock
          label="News event trades"
          rows={[
            ["Count", d.n_news],
            ["Win Rate", fmtPct(d.news_wr)],
            ["Avg net P&L", fmt$(d.news_avg_net)],
          ]}
          accentColor={d.news_avg_net >= 0 ? C.green : C.red}
        />
        <StatBlock
          label="Normal trades"
          rows={[
            ["Count", d.n_normal],
            ["Win Rate", fmtPct(d.normal_wr)],
            ["Avg net P&L", fmt$(d.normal_avg_net)],
          ]}
          accentColor={d.normal_avg_net >= 0 ? C.green : C.red}
        />
      </div>
      <div style={{
        padding: "10px 14px",
        background: isPositive ? C.green + "10" : C.red + "10",
        border: `1px solid ${isPositive ? C.green : C.red}30`,
        borderRadius: 8,
        fontSize: 12,
        color: C.text,
        lineHeight: 1.6,
      }}>
        {isPositive
          ? `News event trades outperform baseline by ${fmt$(Math.abs(d.news_avg_net - d.normal_avg_net))}/trade. Consider sizing up slightly during high-impact events.`
          : `News event trades underperform baseline by ${fmt$(Math.abs(d.news_avg_net - d.normal_avg_net))}/trade (${Math.abs(d.wr_gap_pp ?? 0).toFixed(0)}pp win rate gap). Consider sitting out news events or reducing size.`
        }
      </div>
    </div>
  );
}

function ManualExitDetails({ d }) {
  if (!d || d.n_manual == null) return <NeedsData msg="Log exit_reason for your trades to see this analysis." />;

  const isPositive = d.edge_direction === "positive";
  return (
    <div>
      <div className="fos-grid-2col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
        <StatBlock
          label="Manual exits"
          rows={[
            ["Count", d.n_manual],
            ["Win Rate", fmtPct(d.manual_wr)],
            ["Avg net P&L", fmt$(d.manual_avg_net)],
          ]}
          accentColor={d.manual_avg_net >= 0 ? C.green : C.red}
        />
        <StatBlock
          label="Plan-based exits"
          rows={[
            ["Count", d.n_planned],
            ["Win Rate", fmtPct(d.planned_wr)],
            ["Avg net P&L", fmt$(d.planned_avg_net)],
          ]}
          accentColor={d.planned_avg_net >= 0 ? C.green : C.red}
        />
      </div>
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 12 }}>
        Override rate: <strong style={{ color: C.text }}>{fmtPct(d.override_rate)}</strong> of exits are manual
        ({d.n_manual} of {d.n_manual + d.n_planned} tagged exits)
      </div>
      <div style={{
        padding: "10px 14px",
        background: isPositive ? C.green + "10" : C.red + "10",
        border: `1px solid ${isPositive ? C.green : C.red}30`,
        borderRadius: 8,
        fontSize: 12,
        color: C.text,
        lineHeight: 1.6,
      }}>
        {isPositive
          ? `Your manual exits beat plan-based exits by ${fmt$(Math.abs(d.edge_diff))}/trade. Your read on exits is better than your initial plan.`
          : `Your manual exits underperform plan-based exits by ${fmt$(Math.abs(d.edge_diff))}/trade. Trusting your original stop/target more often would improve results.`
        }
      </div>
    </div>
  );
}

function MarketRegimeDetails({ d }) {
  if (!d || !d.by_condition?.length) return <NeedsData msg="Log market_condition for your trades to see regime analysis." />;

  return (
    <div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 14 }}>
        {[...d.by_condition].sort((a, b) => b.avg_net - a.avg_net).map(cond => (
          <div key={cond.condition} style={{
            display: "grid",
            gridTemplateColumns: "120px 50px 1fr 90px",
            gap: 10,
            alignItems: "center",
            fontSize: 12,
            padding: "8px 10px",
            background: "#0b0d19",
            borderRadius: 6,
          }}>
            <span style={{ color: C.text, fontWeight: 600 }}>{cond.condition}</span>
            <span style={{ color: C.muted, fontSize: 11 }}>n={cond.n}</span>
            <span style={{ color: C.muted, fontSize: 11 }}>WR {fmtPct(cond.wr)}</span>
            <Pill value={cond.avg_net} />
          </div>
        ))}
      </div>
      {d.is_leaking && (
        <div style={{
          padding: "10px 14px",
          background: C.red + "10",
          border: `1px solid ${C.red}30`,
          borderRadius: 8,
          fontSize: 12,
          color: C.text,
          lineHeight: 1.6,
        }}>
          {d.worst.condition} markets cost you {fmt$(Math.abs(d.worst.avg_net))}/trade on average.
          Consider reducing size or sitting out {d.worst.condition} sessions until you identify
          why your edge disappears in this regime.
        </div>
      )}
    </div>
  );
}

function SetupEdgeDetails({ d }) {
  if (!d || !d.setups?.length) return <NeedsData msg="Log setup_type for your trades to see setup ranking." />;

  return (
    <div>
      <div style={{ fontSize: 11, color: C.muted, marginBottom: 10 }}>
        Setups ranked by avg net P&L/trade. CI = 95% confidence interval.
        Grayed = insufficient data (n &lt; 5).
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 14 }}>
        {d.setups.map(s => {
          const hasData = s.n >= 5;
          const color   = s.reliable_positive ? C.green : s.reliable_negative ? C.red : C.text;
          return (
            <div key={s.name} style={{
              display: "grid",
              gridTemplateColumns: "1fr 50px 80px 90px",
              gap: 8,
              alignItems: "center",
              fontSize: 12,
              padding: "8px 10px",
              background: "#0b0d19",
              borderRadius: 6,
              opacity: hasData ? 1 : 0.45,
            }}>
              <span style={{ color, fontWeight: s.reliable_positive || s.reliable_negative ? 700 : 400 }}>
                {s.name.replace(/_/g, " ")}
              </span>
              <span style={{ color: C.muted, fontSize: 11 }}>n={s.n}</span>
              <span style={{ color: C.muted, fontSize: 11 }}>WR {fmtPct(s.wr)}</span>
              <Pill value={s.avg_net} />
            </div>
          );
        })}
      </div>
      {d.leaking_setups?.length > 0 && (
        <div style={{
          padding: "10px 14px",
          background: C.red + "10",
          border: `1px solid ${C.red}30`,
          borderRadius: 8,
          fontSize: 12,
          color: C.text,
          lineHeight: 1.6,
        }}>
          <strong>{d.leaking_setups.map(s => s.name.replace(/_/g, " ")).join(", ")}</strong>
          {d.leaking_setups.length === 1 ? " is" : " are"} statistically net-negative with enough
          data to be confident. Consider pausing {d.leaking_setups.length === 1 ? "this setup" : "these setups"}
          for 30 days and reviewing why it underperforms.
        </div>
      )}
    </div>
  );
}

// ─── Shared sub-components ────────────────────────────────────────────────────
function StatBlock({ label, rows, accentColor }) {
  return (
    <div style={{ padding: "12px 14px", background: "#0b0d19", borderRadius: 8 }}>
      <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>
        {label}
      </div>
      {rows.map(([k, v]) => (
        <div key={k} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
          <span style={{ color: C.muted }}>{k}</span>
          <span style={{ fontFamily: "monospace", color: accentColor || C.text, fontWeight: 600 }}>{v}</span>
        </div>
      ))}
    </div>
  );
}

function fmtPct(v) {
  if (v == null || isNaN(v)) return "—";
  return `${(v * 100).toFixed(0)}%`;
}

// ─────────────────────────────────────────────────────────────────────────────
// DetectorCard — same shell as BehaviorAnalytics.DetectorCard
// ─────────────────────────────────────────────────────────────────────────────
function DetectorCard({ detector, state, accountId, onStatusChange, snapshotsMap, sinceDays }) {
  const { isMobile } = useViewport();
  const [open, setOpen] = useState(false);

  const hasCost   = detector.estimated_cost > 0;
  const hasData   = detector.confidence !== "insufficient";
  const status    = state?.status || "active";
  const isDismissed = status === "dismissed";

  // Progress toward target (if working_on_it)
  const descriptor = getTargetDescriptor(detector.id);
  const progress   = status === "working_on_it" && descriptor && state
    ? computeProgress(state, detector)
    : null;

  return (
    <div style={{
      padding: "16px 20px",
      background: C.panel,
      border: `1px solid ${C.border}`,
      borderRadius: 12,
      opacity: isDismissed ? 0.45 : 1,
      marginBottom: 8,
    }}>
      {/* Card header */}
      <div style={{
        display: "flex",
        alignItems: isMobile ? "flex-start" : "flex-start",
        flexDirection: isMobile ? "column" : "row",
        gap: 16,
        flexWrap: "wrap",
      }}>
        {/* Left: title + badges */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
            <span style={{ fontSize: 15, fontWeight: 700, color: C.text }}>{detector.title}</span>
            <ConfidenceBadge level={detector.confidence} />
            {status !== "active" && <StatusPill status={status} />}
          </div>
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 6 }}>{detector.subtitle}</div>
          {detector.confidence === "thin" && <EarlySignalBanner detector={detector} />}
          {hasData && (
            <div style={{ fontSize: 13, color: C.sub, fontStyle: "italic" }}>{detector.summary}</div>
          )}
        </div>

        {/* Right: cost + n + kebab */}
        <div style={{
          minWidth: isMobile ? 0 : 140,
          textAlign: isMobile ? "left" : "right",
          display: "flex",
          flexDirection: "column",
          alignItems: isMobile ? "flex-start" : "flex-end",
        }}>
          {hasCost && hasData && status !== "acknowledged" && status !== "dismissed" && (
            <>
              <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                Est. Cost / Month
              </div>
              <div style={{ fontSize: 18, fontWeight: 800, color: C.red, fontFamily: "monospace" }}>
                {fmt$(-detector.estimated_cost)}
              </div>
            </>
          )}
          {!hasCost && hasData && (
            <div style={{ fontSize: 11, color: C.green, fontWeight: 600 }}>Informational</div>
          )}
          <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>n = {detector.n}</div>
        </div>

        {/* Kebab menu */}
        <DetectorActionsMenu
          detector={detector}
          state={state}
          accountId={accountId}
          onChange={onStatusChange}
        />
      </div>

      {/* Insufficient-data state: progress bar + what's coming. Replaces the
          old "n = X / Y needed" stub so trial/new users see momentum. */}
      {!hasData && <InsufficientStatePanel detector={detector} />}

      {/* Progress bar if working_on_it */}
      {progress != null && (
        <div style={{ marginTop: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: C.muted, marginBottom: 4 }}>
            <span>Progress toward target</span>
            <span style={{ fontFamily: "monospace" }}>{Math.round(progress * 100)}%</span>
          </div>
          <div style={{ height: 6, background: C.border, borderRadius: 3, overflow: "hidden" }}>
            <div style={{
              height: "100%",
              width: `${Math.min(100, Math.max(0, progress * 100))}%`,
              background: progress >= 1 ? C.green : C.blue,
              transition: "width 0.3s ease",
            }} />
          </div>
        </div>
      )}

      {/* Sparkline */}
      {hasData && (
        <SparklineSection
          detector={detector}
          snapshotsMap={snapshotsMap}
          sinceDays={sinceDays}
        />
      )}

      {/* Expand / collapse detail */}
      {hasData && (
        <div style={{ marginTop: 12 }}>
          <button
            onClick={() => setOpen(o => !o)}
            style={{
              background: "none", border: "none", color: C.blue,
              cursor: "pointer", fontSize: 12, padding: 0, fontWeight: 600,
            }}
          >
            {open ? "▼ Hide details" : "▶ Show details"}
          </button>
          {open && (
            <div style={{ marginTop: 14, borderTop: `1px solid ${C.border}`, paddingTop: 14 }}>
              {detector.id === "news_event"    && <NewsEventDetails d={detector.details} />}
              {detector.id === "manual_exit"   && <ManualExitDetails d={detector.details} />}
              {detector.id === "market_regime" && <MarketRegimeDetails d={detector.details} />}
              {detector.id === "setup_edge"    && <SetupEdgeDetails d={detector.details} />}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ContextAnalytics — main export
// ─────────────────────────────────────────────────────────────────────────────
export function ContextAnalytics({ trades = [], userId, accountId }) {
  const [sinceDays, setSinceDays] = useState(90);

  // Run detectors server-side
  const { data: contextResult, loading: detectorsLoading, error: detectorsError } = useDetectors({
    suite: "context",
    trades,
    userId,
  });

  // All hooks must run unconditionally on every render. Keep them at the top
  // of the component, BEFORE any early returns (Rules of Hooks).
  //
  // `detectors` is wrapped in useMemo so the empty-array fallback has a
  // stable identity across renders — without it, useEffect below would
  // re-fire every render because [] !== [] by reference.
  const detectors = useMemo(() => contextResult?.detectors ?? [], [contextResult]);

  // Detector state (acknowledge/dismiss/working-on-it)
  const { resolve, setStatus } = useDetectorStates(userId);

  // Sparkline history
  const { map: snapshotsMap } = useDetectorSnapshotsMap({ userId, accountId, sinceDays });

  // Write today's snapshots on mount (same pattern as BehaviorAnalytics)
  useEffect(() => {
    if (!userId || !accountId || !detectors.length) return;
    writeSnapshots({ userId, accountId, detectors }).catch(e =>
      console.warn("[ContextAnalytics] snapshot write failed:", e)
    );
  }, [userId, accountId, detectors]);

  if (detectorsLoading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 60, color: "#8892a4", fontSize: 14 }}>
        <span style={{ marginRight: 10 }}>⚙️</span> Analyzing patterns…
      </div>
    );
  }

  if (detectorsError && detectorsError !== "subscription_required") {
    return (
      <div style={{ padding: 32, textAlign: "center", color: "#ff6b6b", fontSize: 13 }}>
        Error loading detectors: {detectorsError}
      </div>
    );
  }

  const handleStatusChange = async (args) => {
    await setStatus({ ...args, allowUserWide: false });
  };

  // Combined headline cost (excludes acknowledged/dismissed)
  const headlineCost = detectors
    .filter(d => {
      const s = resolve(d.id, accountId)?.status;
      return s !== "acknowledged" && s !== "dismissed";
    })
    .reduce((s, d) => s + (d.estimated_cost || 0), 0);

  const hasAnyData = detectors.some(d => d.confidence !== "insufficient");

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {/* Header */}
      <div style={{ marginBottom: 8 }}>
        <SectionTitle>Context Analytics</SectionTitle>
        <p style={{ fontSize: 12, color: C.muted, margin: "4px 0 0" }}>
          How your trading context affects outcomes — news events, market conditions,
          exit behavior, and setup selection.
        </p>
      </div>

      {/* Combined cost banner */}
      {headlineCost > 0 && (
        <Card style={{
          padding: "14px 18px",
          background: "#1a0b0b",
          border: `1px solid ${C.red}40`,
          marginBottom: 8,
        }}>
          <div style={{ fontSize: 11, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>
            Combined Estimated Cost
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 8 }}>
            <div style={{ fontSize: 11, color: C.sub }}>Observed rate, extrapolated to a monthly figure</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: C.red, fontFamily: "monospace" }}>
              {fmt$(-headlineCost)} / month
            </div>
          </div>
        </Card>
      )}

      {/* Sparkline range selector */}
      {hasAnyData && (
        <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
          {[30, 60, 90].map(d => (
            <button
              key={d}
              onClick={() => setSinceDays(d)}
              style={{
                padding: "4px 12px", borderRadius: 6,
                border: `1px solid ${sinceDays === d ? C.blue : C.border}`,
                background: sinceDays === d ? C.blue + "22" : "transparent",
                color: sinceDays === d ? C.blue : C.muted,
                fontSize: 12, fontWeight: 600, cursor: "pointer",
              }}
            >
              {d}d
            </button>
          ))}
        </div>
      )}

      {/* Detector cards */}
      {detectors.map(detector => (
        <DetectorCard
          key={detector.id}
          detector={detector}
          state={resolve(detector.id, accountId)}
          accountId={accountId}
          onStatusChange={handleStatusChange}
          snapshotsMap={snapshotsMap}
          sinceDays={sinceDays}
        />
      ))}

      {/* Empty state */}
      {!hasAnyData && (
        <Card style={{ padding: 32, textAlign: "center" }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>🔍</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 8 }}>
            Not enough context data yet
          </div>
          <div style={{ fontSize: 13, color: C.muted, maxWidth: 400, margin: "0 auto", lineHeight: 1.6 }}>
            Log <strong>market condition</strong>, <strong>setup type</strong>, and{" "}
            <strong>exit reason</strong> when entering trades. With 10+ trades tagged, 
            these detectors will show you how your context affects your edge.
          </div>
        </Card>
      )}
    </div>
  );
}
